import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';
import { MODAL_TYPES, useUIStore, type ModalType } from '@/stores/ui-store';

type SearchRecord = Record<string, unknown>;

// Transient modals that don't need URL persistence (ephemeral confirmations)
const TRANSIENT_MODALS: readonly ModalType[] = ['confirm-delete'];

function parseModalType(value: unknown): ModalType | null {
  if (typeof value !== 'string') return null;
  return MODAL_TYPES.includes(value as ModalType) ? (value as ModalType) : null;
}

function unsetSearchKey(search: SearchRecord, key: string): SearchRecord {
  if (!(key in search)) return search;
  const next = { ...search };
  delete next[key];
  return next;
}

export function GlobalUiRouteSync(): null {
  const navigate = useNavigate({ from: '/' });
  const search = useRouterState({
    select: (state) => state.location.search as SearchRecord,
  });

  const rawModal = typeof search.modal === 'string' ? search.modal : null;
  const modalFromUrl = useMemo(() => parseModalType(rawModal), [rawModal]);

  const modalType = useUIStore((state) => state.modal.type);

  const openModal = useUIStore((state) => state.openModal);
  const closeModal = useUIStore((state) => state.closeModal);
  const lastModalFromUrlRef = useRef<ModalType | null | undefined>(modalFromUrl);

  // Clean unknown query params to avoid leaving broken URLs around.
  useEffect(() => {
    const hasInvalidModal = rawModal !== null && modalFromUrl === null;
    if (!hasInvalidModal) return;

    navigate({
      replace: true,
      search: (prev: SearchRecord) => {
        return unsetSearchKey(prev, 'modal');
      },
    });
  }, [modalFromUrl, navigate, rawModal]);

  // URL -> Zustand (only react when the URL changes)
  useEffect(() => {
    if (lastModalFromUrlRef.current === modalFromUrl) return;
    lastModalFromUrlRef.current = modalFromUrl;

    if (modalFromUrl && modalFromUrl !== modalType) {
      openModal(modalFromUrl, {});
      return;
    }

    if (!modalFromUrl && modalType) {
      closeModal();
    }
  }, [closeModal, modalFromUrl, modalType, openModal]);

  // Zustand -> URL
  // Skip URL sync for transient modals like confirm-delete that don't need URL persistence
  useEffect(() => {
    if (modalType === modalFromUrl) return;
    // Don't sync transient modals to URL - they're ephemeral confirmations
    if (modalType && TRANSIENT_MODALS.includes(modalType)) return;

    navigate({
      replace: modalType === null,
      search: (prev: SearchRecord) => {
        if (modalType) {
          return { ...prev, modal: modalType };
        }
        return unsetSearchKey(prev, 'modal');
      },
    });
  }, [modalFromUrl, modalType, navigate]);

  return null;
}
